/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from "react";
import Button from "../../../components/Button/Button";
import styles from "./Customers.module.css";
import { Add, Delete, ArrowDropDown, ArrowDropUp, WhatsApp, Edit } from "@mui/icons-material";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { deleteClient, loadAllClients } from "../../../services/customers";
import TableContainer from "../../../components/TableContainer/TableContainer";
import ModalDelete from "../../../components/ModalDelete/ModalDelete";
import InputSearch from "../../../components/InputSearch/InputSearch";
import { useIsMobile } from "../../../utils/useIsMobile";

export interface ClientFormData {
  uid_cliente?: string;
  nome: string;
  cpf_cnpj?: string;
  inscricao_estadual?: string;
  telefone?: string;
  whatsapp?: string;
  email?: string;
  cep?: string;
  endereco?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  id_municipio?: number | null;
  id_estado?: string | null;
  cidade?: { municipio?: string };
  estado?: { estado?: string };
  nascimento?: string;
  is_mensalista?: boolean;
  saldo_atual?: number;
  limite_credito?: number;
}

interface IorderCustomer {
  nomeEx?: "DESC" | "ASC";
  estadoEx?: "DESC" | "ASC";
  municipioEx?: "DESC" | "ASC";
  telefoneEx?: "DESC" | "ASC";
}

const Customers = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [searchTerm, setSearchTerm] = useState("");
  const [orderTable, setOrderTable] = useState<IorderCustomer>({ nomeEx: "ASC" });
  const [deleteTarget, setDeleteTarget] = useState<{ uid_cliente: string; nome: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const { data, fetchNextPage, hasNextPage, isLoading, isError, refetch } = useInfiniteQuery({
    queryKey: ["clients", searchTerm, orderTable],
    queryFn: ({ pageParam = 1 }) => loadAllClients(searchTerm, pageParam as number, orderTable),
    getNextPageParam: (lastPage: any, allPages) => lastPage.data.hasMore ? allPages.length + 1 : undefined,
    initialPageParam: 1,
  });

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      const res = await deleteClient(deleteTarget.uid_cliente);
      await refetch();
      await queryClient.invalidateQueries({ queryKey: ["clients"] });
      toast.success(res.message || "Cliente excluído com sucesso");
    } catch (error: any) {
      toast.error(error.message || "Erro ao excluir cliente");
    } finally {
      setIsDeleting(false);
      setDeleteTarget(null);
    }
  };

  useEffect(() => {
    if (isError) toast.error("Erro ao carregar clientes");
  }, [isError]);

  function formatWhatsapp(phone: string): string {
    const digits = phone.replace(/\D/g, "");
    return digits.replace(/^(\d{2})(\d{5})(\d{4})$/, "($1) $2-$3");
  }

  const handleChangeOrder = (field: keyof IorderCustomer) => {
    setOrderTable({ [field]: orderTable[field] === "ASC" ? "DESC" : "ASC" });
  };

  const renderSortableHeader = (field: keyof IorderCustomer, label: string) => (
    <Button variant="textButton" onClick={() => handleChangeOrder(field)}
      title={orderTable[field] === "DESC" ? "Decrescente" : "Crescente"}>
      {label}
      {orderTable[field] ? orderTable[field] === "DESC" ? <ArrowDropDown /> : <ArrowDropUp /> : ""}
    </Button>
  );

  const allClients = data?.pages.flatMap((page: any) => page?.data?.clients ?? []) ?? [];

  return (
    <div className={styles.containerProjects}>
      <div className={styles.purchases}>
        <div className={styles.header}>
          <h1>Clientes</h1>
          <InputSearch searchValue={searchTerm} setSearchValue={setSearchTerm} placeholder="Pesquisar" />
          <div className={styles.headerButtons}>
            <p>Qtd: <span>{data?.pages.at(-1)?.data?.qtdClients || 0}</span></p>
            <Button variant="contain" onClick={() => navigate("add")} title="Adicionar novo cliente">
              <Add />
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div>Carregando...</div>
        ) : isMobile ? (
          // ── Mobile: cards ────────────────────────────────────────────────────
          <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: "0 4px" }}>
            {allClients.map((client: any) => (
              <div key={client.uid_cliente}
                style={{ background: "#fff", borderRadius: 12, padding: "14px 16px", boxShadow: "0 1px 6px rgba(0,0,0,0.08)", border: "1px solid #eee", cursor: "pointer" }}
                onClick={() => navigate(`edit/${client.uid_cliente}`)}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                  <span style={{ fontWeight: 700, fontSize: "0.95rem", color: "#222" }}>{client.nome_fantasia}</span>
                </div>
                <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 12 }}>
                  {client.cidade?.municipio && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      <span style={{ fontSize: "0.7rem", color: "#999", textTransform: "uppercase", fontWeight: 600 }}>Cidade</span>
                      <span style={{ fontSize: "0.88rem" }}>{client.cidade.municipio} / {client.estado?.estado}</span>
                    </div>
                  )}
                  {client.telefone && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      <span style={{ fontSize: "0.7rem", color: "#999", textTransform: "uppercase", fontWeight: 600 }}>Telefone</span>
                      <span style={{ fontSize: "0.88rem" }}>{client.telefone}</span>
                    </div>
                  )}
                </div>
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", borderTop: "1px solid #f0f0f0", paddingTop: 10 }}
                  onClick={(e) => e.stopPropagation()}>
                  <Button variant="contain" typeStyle="small" title="WhatsApp"
                    disabled={!client.whatsapp || client.whatsapp.trim() === ""}
                    onClick={() => client.whatsapp && window.open(`https://wa.me/${client.whatsapp}`)}>
                    <WhatsApp fontSize="small" />
                  </Button>
                  <Button variant="contain" typeStyle="small" title="Editar"
                    onClick={() => navigate(`edit/${client.uid_cliente}`)}>
                    <Edit fontSize="small" />
                  </Button>
                  <Button variant="contain" typeStyle={["small", "cancel"] as any} title="Excluir"
                    onClick={() => setDeleteTarget({ uid_cliente: client.uid_cliente, nome: client.nome_fantasia })}>
                    <Delete fontSize="small" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          // ── Desktop: tabela ───────────────────────────────────────────────────
          <TableContainer
            columns={[
              { key: "nomeEx", titulo: renderSortableHeader("nomeEx", "Nome") as any, classStyle: styles.columnNome },
              { key: "estadoEx", titulo: renderSortableHeader("estadoEx", "Estado") as any, classStyle: styles.columnEstado },
              { key: "municipioEx", titulo: renderSortableHeader("municipioEx", "Município") as any, classStyle: styles.columnMunicipio },
              { key: "telefoneEx", titulo: renderSortableHeader("telefoneEx", "Telefone") as any, classStyle: styles.columnTelefone },
              { key: "actionEx", titulo: "Ação", classStyle: styles.columnAction },
            ]}
            content={allClients.map((client: any) => {
              const editUrl = `edit/${client.uid_cliente}`;
              const renderLink = (content: React.ReactNode) => (
                <Link to={editUrl} state={{ clientToEdit: client }}
                  style={{ textDecoration: "none", color: "inherit", display: "block", width: "100%" }}>
                  {content}
                </Link>
              );
              return {
                values: {
                  nomeEx: renderLink(client.nome_fantasia),
                  estadoEx: renderLink(client.estado?.estado),
                  municipioEx: renderLink(client.cidade?.municipio),
                  telefoneEx: renderLink(client.telefone),
                },
                buttons: [
                  {
                    variant: "contain", typeStyle: "small",
                    title: client.whatsapp ? `WhatsApp: ${formatWhatsapp(client.whatsapp)}` : "Sem WhatsApp",
                    disabled: !client.whatsapp || client.whatsapp.trim() === "",
                    onClick: () => client.whatsapp && window.open(`https://wa.me/${client.whatsapp}`),
                    icon: <WhatsApp />,
                  },
                  {
                    variant: "contain", typeStyle: "small", title: "Editar cliente",
                    onClick: () => navigate(editUrl),
                    icon: <Edit />,
                  },
                  {
                    variant: "contain", typeStyle: ["small", "cancel"],
                    title: "Excluir cliente",
                    onClick: () => setDeleteTarget({ uid_cliente: client.uid_cliente, nome: client.nome_fantasia }),
                    icon: <Delete />,
                  },
                ],
              };
            })}
          />
        )}

        <div className={styles.loadMore}>
          {hasNextPage ? (
            <Button variant="contain" onClick={() => fetchNextPage()}>Carregar Mais</Button>
          ) : (
            <p className={styles.tableLastItem}>Todos os registros foram carregados</p>
          )}
        </div>
      </div>

      <ModalDelete
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        isDeleting={isDeleting}
        isMobile={isMobile}
        entity="cliente"
        title={deleteTarget?.nome || ""}
      />
    </div>
  );
};

export default Customers;